/**
 * Convert KYCFormData (camelCase) partial to DBKYBForm (snake_case) for tRPC mutations.
 * Mirrors BFF fieldMapping.uiKeyToDbKey for KYB-specific keys.
 */

const UI_TO_DB_KEY_MAP: Record<string, string> = {
	sanctionsScreenedItems: "sanctions_what_screened",
	monitoringVendor: "blockchain_vendor",
	monitoringProcess: "blockchain_process",
	monitoringThresholds: "blockchain_thresholds",
	blockingCircumstances: "block_circumstances",
	blockingPreventionControls: "prevention_controls",
	ownershipStructureChartUrl: "ownership_chart_url",
	walletHolderInfoCollected: "wallet_holder_info_collection",
	walletHolderInfoUpdateCircumstances: "wallet_holder_update_circumstances",
	walletHolderInfoUpdateFrequency: "wallet_holder_update_frequency",
	currentlyIntegratedWithStellar: "integrated_with_stellar",
	isWalletHolderInfoUpdatedRegularly: "is_wallet_holder_info_updated",
	virtualAssetsDetails: "virtual_assets_info",
	primaryProductContact: "primary_technical_contact",
	proofOfBusinessUrl: "proof_of_business_url",
	proofOfAddressUrl: "proof_of_address_url",
	federalTaxId: "federal_tax_id",
	vatId: "vat_id",
	rutDocument: "rut_document",
	walletType: "wallet_type",
	hostedStatus: "hosted_status",
	allowP2PInternal: "allow_p2p_internal",
	allowP2PExternal: "allow_p2p_external",
	virtualAssetsInfo: "virtual_assets_info",
	publicKeySharing: "public_key_sharing",
	isWalletOwnerKYCRequired: "is_wallet_owner_kyc_required",
	walletHolderInfoCollection: "wallet_holder_info_collection",
	omnibusAccountDescription: "omnibus_account_usage",
	otherFundingMethods: "other_funding_methods",
	numberOfOwnershipTiers: "number_of_tiers",
	ownershipTiers: "tiers",
	consentForBackgroundChecks: "consent_background_checks",
	anyOwnerConvicted: "any_owner_convicted",
	convictionDetails: "conviction_details",
	hasBoardOfDirectors: "has_board_of_directors",
	boardOfDirectors: "board_of_directors",
	isRegulatedEntity: "is_regulated_entity",
	licensesRegistrations: "licenses_registrations",
	complianceOfficer: "compliance_officer",
	sanctionsScreening: "sanctions_screening",
	sanctionsVendor: "sanctions_vendor",
	blockchainMonitoring: "blockchain_monitoring",
	blockchainVendor: "blockchain_vendor",
	blockchainProcess: "blockchain_process",
	blockchainThresholds: "blockchain_thresholds",
	regulatoryEnforcement: "enforcement_actions",
	enforcementDetails: "enforcement_details",
	riskAssessmentUrl: "risk_assessment_url",
	complianceProgramUrl: "compliance_program_url",
	velocityLimits: "velocity_limits",
	transactionLimits: "transaction_limits",
	otherLimits: "other_limits",
	blockWalletHolders: "blocks_wallet_holders",
	primaryBusinessContact: "primary_business_contact",
	primaryLegalContact: "primary_legal_contact",
	primarySigner: "primary_signer",
	registeredAddress: "registered_address",
	businessLegalName: "business_legal_name",
	dba: "dba",
	previousBusinessNames: "previous_business_names",
	entityType: "entity_type",
	countryOfIncorporation: "country_of_incorporation",
	registrationNumber: "registration_number",
	dateOfIncorporation: "date_of_incorporation",
	website: "website",
	businessDescription: "business_description",
	expectedMonthlyVolume: "expected_monthly_volume",
	ownershipData: "ownership_data",
	walletId: "wallet_id",
	currentStep: "current_step",
	percentComplete: "percent_complete",
	lastSavedAt: "last_saved_at",
	submittedBy: "submitted_by",
	submittedAt: "submitted_at",
	approvedBy: "reviewed_by",
	approvedAt: "reviewed_at",
	reviewerComments: "reviewer_notes",
	createdAt: "created_at",
	updatedAt: "updated_at",
	hiddenFieldValues: "hidden_field_values",
};

function camelToSnake(key: string): string {
	return key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
}

function uiKeyToDbKey(key: string): string {
	return UI_TO_DB_KEY_MAP[key] ?? camelToSnake(key);
}

/** Convert Partial<KYCFormData> to Record for tRPC kyb.update (snake_case keys) */
export function kycFormDataToDbUpdates(
	updates: Record<string, unknown>,
): Record<string, unknown> {
	const result: Record<string, unknown> = {};
	for (const [k, v] of Object.entries(updates)) {
		if (k === "id" || v === undefined) continue;
		const dbKey = uiKeyToDbKey(k);
		result[dbKey] = v;
	}
	return result;
}
